import React, { useState, useRef, useEffect } from 'react';
import styles from './CountryDropdown.module.scss';
import { Country } from '../../../../model/bill-settings/bill-settings';

interface CountryDropdownProps {
  countries: Country[];
  selectedCountry?: Country;
  onCountrySelect: (country: Country) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isLoading: boolean
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  countries,
  selectedCountry,
  onCountrySelect,
  placeholder = "country",
  disabled = false,
  className = "",
  isLoading
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter out deleted countries
  const activeCountries = countries.filter(country => !country.isDeleted);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleCountrySelect = (country: Country) => {
    onCountrySelect(country);
    setIsOpen(false);
  };

  return (
    <div className={`${styles.countrySelector} ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`${styles.selectorButton} ${isOpen ? styles.open : ''} ${disabled ? styles.disabled : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className={styles.selectedContent}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
				<mask id="mask0_2519_8491" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
					<rect width="24" height="24" fill="#D9D9D9" />
				</mask>
				<g mask="url(#mask0_2519_8491)">
					<path
						d="M17.6538 19.6538C16.7169 19.6538 15.925 19.3303 15.2781 18.6834C14.6312 18.0365 14.3077 17.2446 14.3077 16.3077C14.3077 15.3707 14.6312 14.5788 15.2781 13.9319C15.925 13.285 16.7169 12.9615 17.6538 12.9615C18.5907 12.9615 19.3826 13.285 20.0296 13.9319C20.6765 14.5788 21 15.3707 21 16.3077C21 17.2446 20.6765 18.0365 20.0296 18.6834C19.3826 19.3303 18.5907 19.6538 17.6538 19.6538ZM17.6529 18.1538C18.1625 18.1538 18.5977 17.9737 18.9586 17.6134C19.3195 17.2532 19.5 16.8183 19.5 16.3086C19.5 15.799 19.3199 15.3638 18.9596 15.0029C18.5994 14.642 18.1644 14.4615 17.6548 14.4615C17.1452 14.4615 16.7099 14.6416 16.349 15.0019C15.9881 15.3621 15.8077 15.7971 15.8077 16.3067C15.8077 16.8163 15.9878 17.2516 16.348 17.6125C16.7083 17.9734 17.1432 18.1538 17.6529 18.1538ZM11.3654 17.0576H5.25C5.0375 17.0576 4.85938 16.9857 4.71563 16.8419C4.57188 16.6981 4.5 16.5199 4.5 16.3073C4.5 16.0947 4.57188 15.9166 4.71563 15.7731C4.85938 15.6295 5.0375 15.5577 5.25 15.5577H11.3654C11.5779 15.5577 11.756 15.6296 11.8998 15.7734C12.0435 15.9172 12.1154 16.0954 12.1154 16.308C12.1154 16.5206 12.0435 16.6987 11.8998 16.8423C11.756 16.9858 11.5779 17.0576 11.3654 17.0576ZM6.34613 11.0384C5.40923 11.0384 4.61731 10.715 3.97037 10.0681C3.32346 9.42115 3 8.62923 3 7.69232C3 6.7554 3.32346 5.96348 3.97037 5.31657C4.61731 4.66965 5.40923 4.34619 6.34613 4.34619C7.28304 4.34619 8.07496 4.66965 8.72188 5.31657C9.36879 5.96348 9.69225 6.7554 9.69225 7.69232C9.69225 8.62923 9.36879 9.42115 8.72188 10.0681C8.07496 10.715 7.28304 11.0384 6.34613 11.0384ZM6.34518 9.53847C6.85479 9.53847 7.29005 9.35834 7.65095 8.99809C8.01185 8.63784 8.1923 8.20291 8.1923 7.69329C8.1923 7.18366 8.01218 6.74839 7.65193 6.38749C7.29168 6.02659 6.85673 5.84614 6.3471 5.84614C5.83748 5.84614 5.40223 6.02627 5.04133 6.38654C4.68043 6.74679 4.49998 7.18172 4.49998 7.69134C4.49998 8.20096 4.6801 8.63622 5.04035 8.99712C5.4006 9.35802 5.83554 9.53847 6.34518 9.53847ZM18.75 8.44229H12.6346C12.4221 8.44229 12.244 8.37038 12.1002 8.22657C11.9565 8.08277 11.8846 7.90457 11.8846 7.69197C11.8846 7.47938 11.9565 7.3013 12.1002 7.15772C12.244 7.01413 12.4221 6.94234 12.6346 6.94234H18.75C18.9625 6.94234 19.1406 7.01424 19.2843 7.15804C19.4281 7.30186 19.5 7.48006 19.5 7.69264C19.5 7.90524 19.4281 8.08333 19.2843 8.22692C19.1406 8.3705 18.9625 8.44229 18.75 8.44229Z"
						fill="#2E3336"
					/>
				</g>
			</svg>
            <span className={styles.text}>Filter By</span>
          {selectedCountry ? (
            <div className={styles.countrySection}>
              <img
                src={selectedCountry.imageUrl}
                alt={`${selectedCountry.name} flag`}
                className={styles.countryFlag}
              />
              <span className={styles.countryName}>{selectedCountry.name}</span>
            </div>
          ) : (
            <span className={styles.placeholder}>{isLoading? "Loading..." : placeholder}</span>
          )}
        </div>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.countryList}>
            {activeCountries.map((country) => (
              <button
                key={country.id}
                type="button"
                className={`${styles.countryItem} ${
                  selectedCountry?.id === country.id ? styles.selected : ''
                }`}
                onClick={() => handleCountrySelect(country)}
                role="option"
                aria-selected={selectedCountry?.id === country.id}
              >
                <img
                  src={country.imageUrl}
                  alt={`${country.name} flag`}
                  className={styles.countryFlag}
                />
                <span className={styles.countryName}>{country.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryDropdown;